const ORG_CONTEXT_STORAGE_KEY = 'aptcoder.codely.orgId';
const ORG_READY_MESSAGE = 'APTCODER_CODELY_READY';
const ORG_CONTEXT_MESSAGE = 'APTCODER_CODELY_CONTEXT';
const ACTIVITY_CONTEXT_STORAGE_PREFIX = 'aptcoder.codely.activityContext:';
function runtimePlaceholder(name) {
  return ['PLACEHOLDER', name].join('_');
}

const PLACEHOLDER_ORG_APP_SUFFIX = runtimePlaceholder('VITE_CODELY_ORG_APP_DOMAIN_SUFFIX');
const PLACEHOLDER_PARENT_SUFFIX = runtimePlaceholder('VITE_CODELY_ALLOWED_PARENT_ORIGIN_SUFFIX');
const PLACEHOLDER_MAIN_PORTAL_BASE_URL = runtimePlaceholder('VITE_CODELY_MAIN_PORTAL_BASE_URL');
const DIRECT_LOGIN_ORGS = Object.freeze([
  { orgId: 'coding', label: 'Coding' },
  { orgId: 'noncoding', label: 'Non Coding' },
]);

let readyMessageSent = false;
let activityContext = null;
const activityContextListeners = new Set();

function normalizeOrgId(value) {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();
  return /^[a-z0-9][a-z0-9-]{0,62}$/.test(normalized) ? normalized : '';
}

function renderCodelyConfigError(message) {
  if (!document.body || document.getElementById('codely-runtime-config-error')) {
    return;
  }
  const container = document.createElement('div');
  container.id = 'codely-runtime-config-error';
  container.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:2147483647',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'background:#f8fafc',
    'font-family:Arial,sans-serif',
    'color:#111827',
    'padding:24px',
    'text-align:center',
  ].join(';');
  container.innerHTML = `
    <div style="max-width:480px;border:1px solid #d9e2ec;border-radius:8px;background:#fff;padding:24px;box-shadow:0 12px 30px rgba(15,23,42,.12)">
      <div style="font-weight:700;font-size:20px;margin-bottom:8px">Codely configuration error</div>
      <div style="font-size:14px;line-height:1.5;color:#526174">${message}</div>
    </div>`;
  document.body.appendChild(container);
}

function requireRuntimeConfig(value, placeholder, configName) {
  const raw = String(value || '').trim();
  if (!raw || raw === placeholder) {
    const message = `${configName} is required for Codely runtime configuration.`;
    renderCodelyConfigError(message);
    throw new Error(message);
  }
  return raw;
}

function normalizeDomainSuffix(value, placeholder, configName) {
  return requireRuntimeConfig(value, placeholder, configName)
    .replace(/^\.+|\.+$/g, '')
    .toLowerCase();
}

export function configuredOrgAppDomainSuffix() {
  return normalizeDomainSuffix(
    import.meta.env.VITE_CODELY_ORG_APP_DOMAIN_SUFFIX,
    PLACEHOLDER_ORG_APP_SUFFIX,
    'VITE_CODELY_ORG_APP_DOMAIN_SUFFIX'
  );
}

const DEVICE_SERVER_ENVS = ['local', 'staging', 'prod'];

// Which robot server ("staging"/"prod") pairing points a robot at, read from
// this deployment's domain (staging: app.staging.platform.aptcoder.com,
// prod: edubate.in). Anything that isn't staging is prod. A developer can
// force one in their local .env with VITE_CODELY_DEVICE_ENV (e.g. "local").
export function deviceServerEnv() {
  const override = String(import.meta.env.VITE_CODELY_DEVICE_ENV || '')
    .trim()
    .toLowerCase();
  if (DEVICE_SERVER_ENVS.includes(override)) {
    return override;
  }
  return configuredOrgAppDomainSuffix().split('.').includes('staging') ? 'staging' : 'prod';
}

function configuredParentOriginSuffix() {
  return normalizeDomainSuffix(
    import.meta.env.VITE_CODELY_ALLOWED_PARENT_ORIGIN_SUFFIX,
    PLACEHOLDER_PARENT_SUFFIX,
    'VITE_CODELY_ALLOWED_PARENT_ORIGIN_SUFFIX'
  );
}

function configuredMainPortalBaseUrl() {
  return requireRuntimeConfig(
    import.meta.env.VITE_CODELY_MAIN_PORTAL_BASE_URL,
    PLACEHOLDER_MAIN_PORTAL_BASE_URL,
    'VITE_CODELY_MAIN_PORTAL_BASE_URL'
  );
}

export function setCodelyOrgId(value) {
  const orgId = normalizeOrgId(value);
  if (!orgId) {
    return '';
  }
  sessionStorage.setItem(ORG_CONTEXT_STORAGE_KEY, orgId);
  window.__aptcoderCodelyOrgId = orgId;
  return orgId;
}

export function currentOrgId() {
  return normalizeOrgId(
    sessionStorage.getItem(ORG_CONTEXT_STORAGE_KEY) || window.__aptcoderCodelyOrgId || ''
  );
}

export function clearCodelyOrgId() {
  sessionStorage.removeItem(ORG_CONTEXT_STORAGE_KEY);
  window.__aptcoderCodelyOrgId = '';
}

export function currentCodelyActivityContext() {
  return activityContext ? {
    ...activityContext,
    params: activityContext.params.map((parameter) => ({ ...parameter })),
  } : null;
}

export function onCodelyActivityContext(listener) {
  if (typeof listener !== 'function') {
    return () => {};
  }
  activityContextListeners.add(listener);
  if (activityContext) {
    listener(currentCodelyActivityContext());
  }
  return () => activityContextListeners.delete(listener);
}

export function directLoginOrgOptions() {
  return DIRECT_LOGIN_ORGS.map((org) => ({ ...org }));
}

export function requireOrgId() {
  const orgId = currentOrgId();
  if (!orgId) {
    renderMissingOrgContextMessage();
    throw new Error('Codely organization context is required.');
  }
  return orgId;
}

export function orgApiBaseUrl() {
  return `https://${requireOrgId()}.${configuredOrgAppDomainSuffix()}/api/`;
}

export function maybeOrgApiBaseUrl() {
  const orgId = currentOrgId();
  return orgId ? `https://${orgId}.${configuredOrgAppDomainSuffix()}/api/` : '';
}

export function orgGatewayUrl(path = '') {
  const normalizedPath = String(path || '')
    .replace(/^\/+/, '')
    .replace(/^api\/+/, '');
  return `${orgApiBaseUrl()}${normalizedPath}`;
}

export function orgMainPortalUrl(path = '/') {
  const orgId = currentOrgId();
  if (!orgId) {
    return '';
  }
  const base = new URL(configuredMainPortalBaseUrl());
  base.hostname = `${orgId}.${base.hostname.replace(/^\.+/, '')}`;
  const target = String(path || '/').trim();
  if (target.startsWith('#')) {
    base.pathname = '/';
    base.hash = target;
  } else if (target.startsWith('/#')) {
    base.pathname = '/';
    base.hash = target.slice(1);
  } else {
    base.pathname = target.startsWith('/') ? target : `/${target}`;
  }
  return base.toString();
}

export function appendOrgContextToUrl(value, options = {}) {
  const orgId = currentOrgId();
  if (!orgId || !value) {
    return value;
  }
  const url = new URL(value, window.location.href);
  if (!url.searchParams.get('orgId')) {
    url.searchParams.set('orgId', orgId);
  }
  if (options.embed && !url.searchParams.get('embed')) {
    url.searchParams.set('embed', '1');
  }
  if (url.origin === window.location.origin) {
    return `${url.pathname}${url.search}${url.hash}`;
  }
  return url.toString();
}

export function currentPageReturnTarget(defaultTarget = 'home.html') {
  const currentFile = window.location.pathname.split('/').pop() || defaultTarget;
  return appendOrgContextToUrl(
    `${currentFile}${window.location.search || ''}${window.location.hash || ''}`
  );
}

export function loginRedirectUrl(returnTarget = currentPageReturnTarget()) {
  const loginUrl = new URL('login_redirect.html', window.location.href);
  const orgId = currentOrgId();
  if (orgId) {
    loginUrl.searchParams.set('orgId', orgId);
  }
  loginUrl.searchParams.set('returnTo', normalizeReturnTarget(returnTarget));
  return relativeUrl(loginUrl);
}

export function freshLoginUrl() {
  return 'login_redirect.html';
}

export function loginRedirectReturnTarget(defaultTarget = 'home.html') {
  const params = new URLSearchParams(window.location.search || '');
  return normalizeReturnTarget(params.get('returnTo') || defaultTarget);
}

export function renderMissingOrgContextMessage() {
  if (!document.body || document.getElementById('codely-org-context-error')) {
    return;
  }
  const container = document.createElement('div');
  container.id = 'codely-org-context-error';
  container.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:2147483647',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'background:#f8fafc',
    'font-family:Arial,sans-serif',
    'color:#111827',
    'padding:24px',
    'text-align:center',
  ].join(';');
  container.innerHTML = `
    <div style="max-width:440px;border:1px solid #d9e2ec;border-radius:8px;background:#fff;padding:24px;box-shadow:0 12px 30px rgba(15,23,42,.12)">
      <div style="font-weight:700;font-size:20px;margin-bottom:8px">Organization context required</div>
      <div style="font-size:14px;line-height:1.5;color:#526174">Open Codely from your AptCoder organization workspace so it can route API calls through the correct tenant gateway.</div>
    </div>`;
  document.body.appendChild(container);
}

export function mountDirectLoginOrgSelector(container = document.body) {
  if (!container || document.getElementById('codely-direct-org-selector')) {
    return;
  }

  const selectedOrgId = currentOrgId() || DIRECT_LOGIN_ORGS[0].orgId;
  setCodelyOrgId(selectedOrgId);

  const wrapper = document.createElement('label');
  wrapper.id = 'codely-direct-org-selector';
  wrapper.className = 'codely-direct-org-selector';
  wrapper.innerHTML = `
    <span>Organization</span>
    <select aria-label="Organization">
      ${DIRECT_LOGIN_ORGS.map(
        (org) =>
          `<option value="${org.orgId}"${org.orgId === selectedOrgId ? ' selected' : ''}>${org.label}</option>`
      ).join('')}
    </select>
  `;

  const select = wrapper.querySelector('select');
  select?.addEventListener('change', () => {
    setCodelyOrgId(select.value);
  });

  container.prepend(wrapper);
}

function readOrgFromUrl() {
  const params = new URLSearchParams(window.location.search || '');
  return normalizeOrgId(params.get('orgId') || params.get('org') || params.get('organization'));
}

function normalizeReturnTarget(value) {
  const target = String(value || '').trim();
  if (!target) {
    return appendOrgContextToUrl('home.html');
  }
  const url = new URL(target, window.location.href);
  if (url.origin !== window.location.origin) {
    return appendOrgContextToUrl('home.html');
  }
  return relativeUrl(url);
}

function relativeUrl(url) {
  const currentDirectory = window.location.pathname.replace(/[^/]*$/, '');
  const pathname = url.pathname.startsWith(currentDirectory)
    ? url.pathname.slice(currentDirectory.length)
    : url.pathname.replace(/^\/+/, '');
  return `${pathname || 'home.html'}${url.search}${url.hash}`;
}

function isTrustedParentOrigin(origin) {
  if (!origin || origin === window.location.origin) {
    return true;
  }
  try {
    const host = new URL(origin).hostname.toLowerCase();
    const suffix = configuredParentOriginSuffix();
    return host === suffix || host.endsWith(`.${suffix}`);
  } catch {
    return false;
  }
}

function normalizeActivityContext(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const page = String(value.page || '').trim().replace(/^\/+/, '');
  if (!page || page.length > 255 || page.includes('?') || page.includes('#') || page.includes('\\') || page.includes('..')) {
    return null;
  }
  const params = Array.isArray(value.params) ? value.params : [];
  if (params.length > 25) {
    return null;
  }
  const normalizedParams = [];
  const keys = new Set();
  for (const parameter of params) {
    if (!parameter || typeof parameter !== 'object') return null;
    const key = String(parameter.key || '').trim();
    const paramValue = String(parameter.value || '');
    const keyIdentity = key.toLowerCase();
    if (keyIdentity === 'orgid' || keyIdentity === 'embed' || keyIdentity === 'activityurl' || keyIdentity === 'activitycontextid') {
      continue;
    }
    if (!/^[A-Za-z][A-Za-z0-9_-]{0,63}$/.test(key) || keys.has(keyIdentity) || paramValue.length > 1024 || /[\r\n]/.test(paramValue)) {
      return null;
    }
    keys.add(keyIdentity);
    normalizedParams.push({ key, value: paramValue });
  }
  const data = value.data == null ? '' : String(value.data);
  if (data.length > 512 * 1024) {
    return null;
  }
  const instruction = value.instruction == null ? '' : String(value.instruction);
  if (instruction.length > 512 * 1024) {
    return null;
  }
  return { page, params: normalizedParams, data, instruction };
}

function setCodelyActivityContext(value) {
  const normalized = normalizeActivityContext(value);
  if (!normalized) {
    return null;
  }
  activityContext = normalized;
  const snapshot = currentCodelyActivityContext();
  activityContextListeners.forEach((listener) => listener(snapshot));
  return snapshot;
}

function handleParentMessage(event) {
  if (!isTrustedParentOrigin(event.origin)) {
    return;
  }
  const data = event.data || {};
  if (data.type === ORG_CONTEXT_MESSAGE) {
    setCodelyOrgId(data.orgId);
  }
}

function readActivityContextFromUrl() {
  const params = new URLSearchParams(window.location.search || '');
  const contextId = String(params.get('activityContextId') || '').trim();
  if (!/^[A-Za-z0-9._:-]{1,128}$/.test(contextId)) {
    return null;
  }
  const key = `${ACTIVITY_CONTEXT_STORAGE_PREFIX}${contextId}`;
  let parsed = null;
  try {
    const raw = sessionStorage.getItem(key);
    sessionStorage.removeItem(key);
    parsed = raw ? JSON.parse(raw) : null;
  } catch {
    parsed = null;
  }
  return setCodelyActivityContext(parsed);
}

export function notifyParentReady() {
  if (readyMessageSent || window.parent === window) {
    return;
  }
  readyMessageSent = true;
  window.parent.postMessage({ type: ORG_READY_MESSAGE, orgId: currentOrgId() || undefined }, '*');
}

export function initCodelyOrgContext() {
  setCodelyOrgId(readOrgFromUrl());
  readActivityContextFromUrl();
  window.addEventListener('message', handleParentMessage);
  notifyParentReady();
}

initCodelyOrgContext();
